import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Instagram, Target, ListChecks, FileText, Activity, Trash2, Plus, Loader2, X, Edit3, Save } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Vue d'ensemble", icon: Activity },
  { id: "accounts", label: "Comptes IG", icon: Instagram },
  { id: "tasks", label: "Tâches", icon: ListChecks },
  { id: "goals", label: "Objectifs", icon: Target },
  { id: "notes", label: "Notes", icon: FileText },
];

export default function ModelDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [model, setModel] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);

  const refresh = async () => {
    try {
      const [m, a] = await Promise.all([api.crmGetModel(id), api.igListAccounts()]);
      setModel(m);
      setAccounts(a.filter(x => x.model_id === id));
    } catch (e) {
      toast.error("Modèle introuvable");
      nav("/models");
    }
  };

  useEffect(() => { refresh(); }, [id]);

  const save = async (data) => {
    try { await api.crmUpdateModel(id, data); toast.success("Mis à jour"); refresh(); setEditing(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const remove = async () => {
    if (!confirm("Supprimer définitivement cette modèle ?")) return;
    try { await api.crmDeleteModel(id); toast.success("Supprimée"); nav("/models"); }
    catch { toast.error("Erreur"); }
  };

  if (!model) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fadeUp" data-testid="model-detail-page">
      <Link to="/models" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-[#CCFF00]">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour au roster
      </Link>

      <header className="flex items-start gap-5 flex-wrap">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold text-3xl shrink-0">
          {model.name.slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-1">// FICHE MODÈLE</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight" data-testid="model-name">{model.name}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            {model.instagram_handle && <Tag>@{model.instagram_handle}</Tag>}
            <Tag color={model.status === "active" ? "#10B981" : "#F59E0B"}>{model.status}</Tag>
            {model.main_platform && <Tag>{model.main_platform}</Tag>}
            {model.niche && <Tag>{model.niche}</Tag>}
            {model.target_country && <Tag>{model.target_country}</Tag>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditing(!editing)} data-testid="edit-model-btn"
            className="text-xs font-mono uppercase tracking-wider px-3 py-2 border border-[#1E1E24] hover:border-white/30 text-zinc-300 rounded-sm flex items-center gap-1.5">
            <Edit3 className="w-3 h-3" /> {editing ? "Annuler" : "Modifier"}
          </button>
          <button onClick={remove} data-testid="delete-model-btn"
            className="text-xs font-mono uppercase tracking-wider px-3 py-2 border border-red-500/20 text-red-400 hover:bg-red-500/10 rounded-sm flex items-center gap-1.5">
            <Trash2 className="w-3 h-3" /> Supprimer
          </button>
        </div>
      </header>

      {editing && <EditForm model={model} onSave={save} onCancel={() => setEditing(false)} />}

      <div className="border-b border-[#1E1E24] flex gap-0 overflow-x-auto">
        {TABS.map(t => {
          const I = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs font-mono uppercase tracking-wider transition border-b-2 whitespace-nowrap ${
                tab === t.id ? "text-[#CCFF00] border-[#CCFF00]" : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}>
              <I className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <Overview model={model} accounts={accounts} />}
      {tab === "accounts" && <AccountsTab accounts={accounts} />}
      {tab === "tasks" && <TasksTab modelId={id} />}
      {tab === "goals" && <GoalsTab modelId={id} />}
      {tab === "notes" && <NotesTab modelId={id} />}
    </div>
  );
}

const Tag = ({ children, color = "#CCFF00" }) => (
  <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm" style={{ backgroundColor: color + "20", color }}>{children}</span>
);

function Overview({ model, accounts }) {
  const totalFollowers = accounts.reduce((s, a) => s + (a.followers || 0), 0);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card title="Comptes Instagram" v={accounts.length} sub="liés à cette modèle" />
      <Card title="Total abonnés" v={totalFollowers.toLocaleString("fr-FR")} sub="cumulé tous comptes" />
      <Card title="Plateforme" v={model.main_platform} sub="canal principal" />
      {model.tracking_link && (
        <div className="md:col-span-3 border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4">
          <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">// LIEN TRACKING</div>
          <a href={model.tracking_link} target="_blank" rel="noreferrer" className="text-sm text-[#CCFF00] hover:underline break-all">{model.tracking_link}</a>
        </div>
      )}
      {model.objectives && (
        <div className="md:col-span-3 border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4">
          <div className="text-[10px] font-mono uppercase text-zinc-500 mb-2">// OBJECTIFS GÉNÉRAUX</div>
          <p className="text-sm text-zinc-300 whitespace-pre-line">{model.objectives}</p>
        </div>
      )}
      {model.private_notes && (
        <div className="md:col-span-3 border border-orange-500/20 bg-orange-500/[0.02] rounded-sm p-4">
          <div className="text-[10px] font-mono uppercase text-orange-400 mb-2">// NOTES PRIVÉES</div>
          <p className="text-sm text-zinc-300 whitespace-pre-line">{model.private_notes}</p>
        </div>
      )}
    </div>
  );
}

const Card = ({ title, v, sub }) => (
  <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5">
    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">{title}</div>
    <div className="font-mono text-3xl text-white font-bold">{v}</div>
    <div className="text-[10px] font-mono uppercase text-zinc-600 mt-1">{sub}</div>
  </div>
);

function AccountsTab({ accounts }) {
  if (accounts.length === 0) {
    return <div className="border border-dashed border-[#1E1E24] rounded-sm p-10 text-center text-zinc-500 text-sm">
      Aucun compte IG lié. <Link to="/instagram-accounts" className="text-[#CCFF00] hover:underline">Lier un compte →</Link>
    </div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {accounts.map(a => (
        <Link key={a.id} to="/instagram-accounts" className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4 hover:border-[#CCFF00]/30 transition">
          <div className="flex items-center gap-3">
            <Instagram className="w-5 h-5 text-pink-400" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="text-white font-medium">@{a.handle}</div>
              <div className="text-[10px] font-mono text-zinc-500">{(a.followers || 0).toLocaleString("fr-FR")} abonnés · {a.status}</div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function TasksTab({ modelId }) {
  const [tasks, setTasks] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const refresh = async () => { try { setTasks(await api.crmListTasks({ model_id: modelId })); } catch {} };
  useEffect(() => { refresh(); }, [modelId]);
  const toggle = async (t) => {
    const newStatus = t.status === "done" ? "todo" : "done";
    await api.crmUpdateTask(t.id, { status: newStatus });
    refresh();
  };
  const remove = async (id) => { if (!confirm("Supprimer ?")) return; await api.crmDeleteTask(id); refresh(); };
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => setShowAdd(true)} data-testid="add-task-btn" className="text-xs bg-[#CCFF00] text-black font-medium px-3 py-1.5 rounded-sm hover:bg-[#B3E600] flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Nouvelle tâche
        </button>
      </div>
      {tasks.length === 0 ? <div className="border border-dashed border-[#1E1E24] rounded-sm p-8 text-center text-zinc-500 text-sm">Aucune tâche.</div> :
        <div className="space-y-2">
          {tasks.map(t => (
            <div key={t.id} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-3 flex items-center gap-3">
              <input type="checkbox" checked={t.status === "done"} onChange={() => toggle(t)} className="w-4 h-4 accent-[#CCFF00] cursor-pointer" />
              <div className={`flex-1 ${t.status === "done" ? "line-through text-zinc-500" : "text-white"}`}>
                <div className="text-sm">{t.title}</div>
                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{t.priority} · {t.status}{t.due_date ? ` · échéance ${t.due_date}` : ""}</div>
              </div>
              <button onClick={() => remove(t.id)} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>}
      {showAdd && <AddTaskModal modelId={modelId} onClose={() => setShowAdd(false)} onSuccess={() => { setShowAdd(false); refresh(); }} />}
    </div>
  );
}

function AddTaskModal({ modelId, onClose, onSuccess }) {
  const [f, setF] = useState({ title: "", description: "", priority: "medium", due_date: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.crmCreateTask({ ...f, model_id: modelId }); toast.success("Tâche créée"); onSuccess(); }
    catch { toast.error("Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit} className="bg-[#0A0A0C] border border-[#1E1E24] rounded-sm p-6 w-full max-w-md space-y-3">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display text-xl text-white">Nouvelle tâche</div>
          <button type="button" onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <input data-testid="task-title" required placeholder="Titre" value={f.title} onChange={e => setF({...f, title: e.target.value})}
          className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
        <textarea data-testid="task-desc" placeholder="Description" rows={2} value={f.description} onChange={e => setF({...f, description: e.target.value})}
          className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
        <div className="grid grid-cols-2 gap-2">
          <select value={f.priority} onChange={e => setF({...f, priority: e.target.value})}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="low">Priorité basse</option><option value="medium">Moyenne</option><option value="high">Haute</option>
          </select>
          <input type="date" value={f.due_date} onChange={e => setF({...f, due_date: e.target.value})}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white" />
        </div>
        <button disabled={loading} type="submit" data-testid="task-submit"
          className="w-full bg-[#CCFF00] text-black font-medium py-2 rounded-sm hover:bg-[#B3E600] disabled:opacity-50">
          {loading ? "..." : "Créer"}
        </button>
      </form>
    </div>
  );
}

function GoalsTab({ modelId }) {
  const [goals, setGoals] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const refresh = async () => { try { setGoals(await api.crmListCrmGoals({ model_id: modelId })); } catch {} };
  useEffect(() => { refresh(); }, [modelId]);
  const remove = async (id) => { if (!confirm("Supprimer ?")) return; await api.crmDeleteCrmGoal(id); refresh(); };
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => setShowAdd(true)} data-testid="add-goal-btn" className="text-xs bg-[#CCFF00] text-black font-medium px-3 py-1.5 rounded-sm hover:bg-[#B3E600] flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Nouvel objectif
        </button>
      </div>
      {goals.length === 0 ? <div className="border border-dashed border-[#1E1E24] rounded-sm p-8 text-center text-zinc-500 text-sm">Aucun objectif.</div> :
        <div className="space-y-2">
          {goals.map(g => {
            const pct = Math.min(100, (g.current_value / Math.max(1, g.target_value)) * 100);
            return (
              <div key={g.id} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-white text-sm font-medium">{g.title}</div>
                    <div className="text-[10px] font-mono text-zinc-500 mt-1">{g.metric} · {g.period}</div>
                  </div>
                  <button onClick={() => remove(g.id)} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 bg-[#1E1E24] rounded-full overflow-hidden">
                    <div className="h-full bg-[#CCFF00]" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="font-mono text-xs text-[#CCFF00]">{g.current_value}/{g.target_value}</div>
                </div>
              </div>
            );
          })}
        </div>}
      {showAdd && <AddGoalModal modelId={modelId} onClose={() => setShowAdd(false)} onSuccess={() => { setShowAdd(false); refresh(); }} />}
    </div>
  );
}

function AddGoalModal({ modelId, onClose, onSuccess }) {
  const [f, setF] = useState({ title: "", metric: "followers", target_value: 0, current_value: 0, period: "week", deadline: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.crmCreateCrmGoal({ ...f, target_value: Number(f.target_value), current_value: Number(f.current_value), model_id: modelId });
      toast.success("Objectif créé"); onSuccess(); }
    catch { toast.error("Erreur"); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit} className="bg-[#0A0A0C] border border-[#1E1E24] rounded-sm p-6 w-full max-w-md space-y-3">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display text-xl text-white">Nouvel objectif</div>
          <button type="button" onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <input data-testid="goal-title" required placeholder="Ex: +100 followers/semaine" value={f.title} onChange={e => setF({...f, title: e.target.value})}
          className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
        <div className="grid grid-cols-2 gap-2">
          <select value={f.metric} onChange={e => setF({...f, metric: e.target.value})} className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            {["followers", "reach", "engagement", "reels_per_week", "dms", "content_tests", "watchtime"].map(m => <option key={m}>{m}</option>)}
          </select>
          <select value={f.period} onChange={e => setF({...f, period: e.target.value})} className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="day">Par jour</option><option value="week">Par semaine</option><option value="month">Par mois</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder="Cible" value={f.target_value} onChange={e => setF({...f, target_value: e.target.value})}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white" />
          <input type="number" placeholder="Actuel" value={f.current_value} onChange={e => setF({...f, current_value: e.target.value})}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white" />
        </div>
        <button disabled={loading} type="submit" data-testid="goal-submit" className="w-full bg-[#CCFF00] text-black font-medium py-2 rounded-sm hover:bg-[#B3E600] disabled:opacity-50">
          {loading ? "..." : "Créer"}
        </button>
      </form>
    </div>
  );
}

function NotesTab({ modelId }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const refresh = async () => { try { setNotes(await api.crmListNotes({ model_id: modelId })); } catch {} };
  useEffect(() => { refresh(); }, [modelId]);
  const add = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    await api.crmCreateNote({ model_id: modelId, title, body });
    setTitle(""); setBody(""); refresh();
  };
  const remove = async (id) => { await api.crmDeleteNote(id); refresh(); };
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4 space-y-2">
        <input data-testid="note-title" placeholder="Titre (optionnel)" value={title} onChange={e => setTitle(e.target.value)}
          className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
        <textarea data-testid="note-body" placeholder="Note..." rows={3} value={body} onChange={e => setBody(e.target.value)}
          className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
        <button type="submit" data-testid="note-submit" className="bg-[#CCFF00] text-black font-medium px-4 py-1.5 rounded-sm hover:bg-[#B3E600] text-xs">Ajouter</button>
      </form>
      {notes.map(n => (
        <div key={n.id} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="text-white font-medium text-sm">{n.title || "Note"}</div>
            <button onClick={() => remove(n.id)} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          <div className="text-sm text-zinc-300 whitespace-pre-line leading-relaxed">{n.body}</div>
          <div className="text-[10px] font-mono text-zinc-600 mt-2">{new Date(n.created_at).toLocaleString("fr-FR")}</div>
        </div>
      ))}
    </div>
  );
}

function EditForm({ model, onSave, onCancel }) {
  const [f, setF] = useState({ ...model });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }}
      className="border border-[#CCFF00]/30 bg-[#CCFF00]/[0.02] rounded-sm p-5 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FieldI label="Nom" v={f.name} on={v => setF({...f, name: v})} />
        <FieldI label="Handle IG" v={f.instagram_handle} on={v => setF({...f, instagram_handle: v})} />
        <SelI label="Plateforme" v={f.main_platform} on={v => setF({...f, main_platform: v})} opts={["OF", "Fansly", "MYM", "Other"]} />
        <SelI label="Statut" v={f.status} on={v => setF({...f, status: v})} opts={["active", "test", "pause", "problem"]} />
        <FieldI label="Niche" v={f.niche} on={v => setF({...f, niche: v})} />
        <FieldI label="Pays" v={f.target_country} on={v => setF({...f, target_country: v})} />
        <FieldI label="Lien tracking" v={f.tracking_link} on={v => setF({...f, tracking_link: v})} />
      </div>
      <AreaI label="Objectifs" v={f.objectives} on={v => setF({...f, objectives: v})} />
      <AreaI label="Notes privées" v={f.private_notes} on={v => setF({...f, private_notes: v})} />
      <div className="flex gap-2">
        <button type="submit" data-testid="save-model" className="bg-[#CCFF00] text-black font-medium px-4 py-2 rounded-sm hover:bg-[#B3E600] flex items-center gap-1.5 text-sm">
          <Save className="w-3.5 h-3.5" /> Enregistrer
        </button>
        <button type="button" onClick={onCancel} className="text-xs font-mono uppercase tracking-wider px-3 py-2 border border-[#1E1E24] text-zinc-400 hover:text-white rounded-sm">Annuler</button>
      </div>
    </form>
  );
}

const FieldI = ({ label, v, on }) => (
  <div>
    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</label>
    <input value={v || ""} onChange={e => on(e.target.value)}
      className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
  </div>
);
const SelI = ({ label, v, on, opts }) => (
  <div>
    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</label>
    <select value={v || ""} onChange={e => on(e.target.value)}
      className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none">
      {opts.map(o => <option key={o}>{o}</option>)}
    </select>
  </div>
);
const AreaI = ({ label, v, on }) => (
  <div>
    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</label>
    <textarea rows={2} value={v || ""} onChange={e => on(e.target.value)}
      className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
  </div>
);
